import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Avatar, 
  Badge, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Tooltip, 
  Typography, 
  Breadcrumbs,
  Link
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminService from '../../services/AdminService';

const AdminTopBar = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const result = await AdminService.getAlerts({
          limit: 100,
          severity: 'critical,warning'
        });
        
        if (result && result.alerts) {
          setAlertCount(result.alerts.length);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
      }
    };
    
    fetchAlerts();
    
    // Refresh alerts every 60 seconds
    const intervalId = setInterval(fetchAlerts, 60000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleOpenNotificationsMenu = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotificationsMenu = () => {
    setAnchorElNotifications(null);
  };
  
  const handleLogout = async () => {
    try {
      await AdminService.logout();
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Force logout even if API fails
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    }
  };
  
  const handleSettings = () => {
    navigate('/admin/settings');
    handleCloseUserMenu();
  };
  
  const handleProfile = () => {
    navigate('/admin/profile');
    handleCloseUserMenu();
  };
  
  // Generate breadcrumbs based on the current path
  const generateBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(path => path);
    
    // Return empty if we're at dashboard
    if (paths.length <= 2 || paths[1] === 'dashboard') {
      return null;
    }
    
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumbs">
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/dashboard');
          }}
        >
          Admin
        </Link>
        {paths.slice(1).map((path, index) => {
          const routeTo = `/admin/${paths.slice(1, index + 2).join('/')}`;
          const isLast = index === paths.slice(1).length - 1;
          
          return isLast ? (
            <Typography key={path} color="text.primary">
              {path.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </Typography>
          ) : (
            <Link
              key={path}
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(routeTo);
              }}
            >
              {path.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const userMenuId = 'admin-user-menu';
  const notificationsMenuId = 'admin-notifications-menu';
  
  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={0}
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}
    >
      <Toolbar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {generateBreadcrumbs()}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton 
              color="inherit" 
              onClick={handleOpenNotificationsMenu}
              sx={{ mx: 1 }}
            >
              <Badge badgeContent={alertCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          {/* Help Button */}
          <Tooltip title="Help">
            <IconButton color="inherit" sx={{ mx: 1 }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
          
          {/* User Menu */}
          <Box>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls={userMenuId}
              aria-haspopup="true"
              onClick={handleOpenUserMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>A</Avatar>
            </IconButton>
            <Menu
              id={userMenuId}
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleProfile}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountCircleIcon sx={{ mr: 1 }} />
                  <Typography>Profile</Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleSettings}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography>Settings</Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  <Typography>Logout</Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
      
      {/* Notifications menu */}
      <Menu
        sx={{ mt: '45px' }}
        id={notificationsMenuId}
        anchorEl={anchorElNotifications}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElNotifications)}
        onClose={handleCloseNotificationsMenu}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
        </Box>
        <MenuItem onClick={handleCloseNotificationsMenu}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="body2" fontWeight="bold">System Performance Alert</Typography>
            <Typography variant="caption" color="text.secondary">CPU usage exceeded 80% threshold for 10 minutes</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>10 minutes ago</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleCloseNotificationsMenu}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="body2" fontWeight="bold">New User Registration</Typography>
            <Typography variant="caption" color="text.secondary">User 'john.doe' has registered and awaits approval</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>30 minutes ago</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleCloseNotificationsMenu}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="body2" fontWeight="bold">Data Source Update Completed</Typography>
            <Typography variant="caption" color="text.secondary">Technical documentation source updated with 42 new files</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>2 hours ago</Typography>
          </Box>
        </MenuItem>
        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Link 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              handleCloseNotificationsMenu();
              navigate('/admin/alerts');
            }}
            sx={{ fontSize: '0.875rem' }}
          >
            View all notifications
          </Link>
        </Box>
      </Menu>
    </AppBar>
  );
};

export default AdminTopBar;